// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {useStoreAccess} from '@app/features/store/state/useStoreAccess';
import {STORE_COPY} from '@app/features/store/utils/StoreCopy';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Spinner} from '@app/features/ui/components/Spinner';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/NameplatePickerModal.module.css';
import {NameplateMedia} from '@app/features/user/components/NameplateMedia';
import NameplateCatalog from '@app/features/user/state/NameplateCatalog';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {CosmeticPickerPreview} from '@app/features/user/components/modals/tabs/my_profile_tab/CosmeticPickerPreview';
import shared from '@app/features/user/components/modals/tabs/my_profile_tab/CosmeticPickerPreview.module.css';
import Users from '@app/features/user/state/Users';
import {CheckIcon} from '@phosphor-icons/react';
import {useMemo, useState} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'Choose a nameplate',
	comment: 'Title of the nameplate picker modal.',
});
const SEARCH_DESCRIPTOR = msg({
	message: 'Search nameplates',
	comment: 'Placeholder for filtering nameplates in the picker modal.',
});
const NONE_DESCRIPTOR = msg({
	message: 'None',
	comment: 'Option that clears the equipped nameplate.',
});
const EMPTY_DESCRIPTOR = msg({
	message: 'No nameplates match that search.',
	comment: 'Empty state when the nameplate picker filter has no results.',
});
const CATALOG_EMPTY_DESCRIPTOR = msg({
	message: 'No nameplates are available yet.',
	comment: 'Empty state when the hosted nameplate catalog has no entries.',
});
const HINT_DESCRIPTOR = msg({
	message: 'Shown behind your name in the member list. Requires {premiumProductName}.',
	comment: 'Helper text in the nameplate picker modal. Preserve {premiumProductName}; it is inserted by code.',
});
const DONE_DESCRIPTOR = msg({
	message: 'Done',
	comment: 'Closes the nameplate picker modal.',
});

interface NameplatePickerModalProps {
	value: string | null;
	onChange: (nameplateId: string | null) => void;
}

export const NameplatePickerModal = observer(function NameplatePickerModal({
	value,
	onChange,
}: NameplatePickerModalProps) {
	const {i18n} = useLingui();
	const [query, setQuery] = useState('');
	const [hoveredId, setHoveredId] = useState<string | null>(null);
	const [selectedId, setSelectedId] = useState<string | null>(value);
	const selectNameplate = (nameplateId: string | null) => {
		setSelectedId(nameplateId);
		onChange(nameplateId);
	};
	const storeAccess = useStoreAccess();
	const accessible = NameplateCatalog.nameplates.filter(
		(entry) => entry.accessible !== false && storeAccess.canUse('nameplate', entry.id),
	);
	const filtered = useMemo(() => {
		const needle = query.trim().toLowerCase();
		if (!needle) {
			return accessible;
		}
		return accessible.filter((entry) => {
			const haystack = `${entry.name} ${entry.description ?? ''}`.toLowerCase();
			return haystack.includes(needle);
		});
	}, [accessible, query]);
	return (
		<Modal.Root onClose={() => ModalCommands.pop()} size="large" data-flx="user.nameplate-picker-modal.root">
			<Modal.ScreenReaderLabel
				text={i18n._(TITLE_DESCRIPTOR)}
				data-flx="user.nameplate-picker-modal.screen-reader-label"
			/>
			<Modal.Header title={i18n._(TITLE_DESCRIPTOR)} data-flx="user.nameplate-picker-modal.header" />
			<Modal.Content data-flx="user.nameplate-picker-modal.content">
				<div className={shared.layout}>
					<div className={shared.listPane}>
						<p className={styles.hint}>{i18n._(HINT_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}</p>
						{accessible.length > 6 ? (
							<Input
								value={query}
								onChange={(event) => setQuery(event.target.value)}
								placeholder={i18n._(SEARCH_DESCRIPTOR)}
								className={styles.search}
								data-flx="user.nameplate-picker-modal.search"
							/>
						) : null}
						{storeAccess.loading ? (
							<div className={styles.empty}>
								<Spinner />
							</div>
						) : accessible.length === 0 ? (
							<div className={styles.empty}>{i18n._(CATALOG_EMPTY_DESCRIPTOR)}</div>
						) : filtered.length === 0 ? (
							<div className={styles.empty}>{i18n._(EMPTY_DESCRIPTOR)}</div>
						) : (
							<div className={clsx(shared.grid, styles.grid)} onMouseLeave={() => setHoveredId(null)}>
								<button
									type="button"
									className={clsx(shared.tile, styles.tile, selectedId == null && shared.tileSelected)}
									onClick={() => selectNameplate(null)}
									onMouseEnter={() => setHoveredId('')}
									data-flx="user.nameplate-picker-modal.none"
								>
									{selectedId == null ? (
										<span className={shared.check}>
											<CheckIcon weight="bold" className={shared.checkIcon} />
										</span>
									) : null}
									<div className={styles.preview} />
									<div className={clsx(shared.tileLabel, styles.label)}>{i18n._(NONE_DESCRIPTOR)}</div>
								</button>
								{filtered.map((entry) => (
									<button
										key={entry.id}
										type="button"
										className={clsx(shared.tile, styles.tile, selectedId === entry.id && shared.tileSelected)}
										onClick={() => selectNameplate(entry.id)}
										onMouseEnter={() => setHoveredId(entry.id)}
										data-flx="user.nameplate-picker-modal.option"
									>
										{selectedId === entry.id ? (
											<span className={shared.check}>
												<CheckIcon weight="bold" className={shared.checkIcon} />
											</span>
										) : null}
										<div className={styles.preview}>
											<NameplateMedia
												src={entry.image}
												catalogId={entry.id}
												active={hoveredId === entry.id}
												className={styles.previewMedia}
												title={entry.name}
											/>
										</div>
										<div className={clsx(shared.tileLabel, styles.label)}>{entry.name}</div>
									</button>
								))}
							</div>
						)}
					</div>
					{(() => {
						const previewId = hoveredId !== null ? hoveredId : selectedId;
						const previewEntry = previewId ? NameplateCatalog.getById(previewId) : undefined;
						const me = Users.currentUser;
						return (
							<CosmeticPickerPreview
								decorationId={me ? (Users.getAvatarDecorationId(me.id) ?? '') : ''}
								nameplateId={previewEntry ? previewEntry.id : null}
								itemName={previewEntry ? previewEntry.name : i18n._(NONE_DESCRIPTOR)}
								itemDescription={previewEntry?.description ?? null}
							/>
						);
					})()}
				</div>
			</Modal.Content>
			<Modal.Footer data-flx="user.nameplate-picker-modal.footer">
				{storeAccess.storeMode && (
					<Button
						variant="secondary"
						onClick={() => {
							ModalCommands.popAll();
							RouterUtils.transitionTo(Routes.STORE);
						}}
						data-flx="user.cosmetic-picker.open-store"
					>
						{i18n._(STORE_COPY.title)}
					</Button>
				)}
				<Button variant="primary" onClick={() => ModalCommands.pop()} data-flx="user.nameplate-picker-modal.done">
					{i18n._(DONE_DESCRIPTOR)}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
